/*  React  */
import React, { createContext, useContext, useEffect, useMemo } from 'react';

const useBroadcastChannel = (channelName: string) => {
    const channel = useMemo(
        () => new BroadcastChannel(channelName),
        [channelName]
    );

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data === 'pageReload') {
                window.location.href = '/';
            }
        };

        channel.onmessage = handleMessage;

        // Clean up the channel when the component unmounts
        return () => {
            channel.close();
        };
    }, [channel]);

    const sendMessage = (msg: string) => {
        channel.postMessage(msg);
    };

    return { sendMessage };
};

type BroadcastChannelContextValue = {
    sendMessage: (msg: string) => void;
};

const defaultFn = () => {};
const defaultContext: BroadcastChannelContextValue = {
    sendMessage: defaultFn
};

export const BroadcastChannelContext = createContext(defaultContext);

export const BroadcastChannelContextProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const broadcast = useBroadcastChannel('pageReload');

    return (
        <BroadcastChannelContext.Provider value={broadcast}>
            {children}
        </BroadcastChannelContext.Provider>
    );
};

export const useBroadcastChannelContext = () => {
    const context = useContext(BroadcastChannelContext);

    if (context === undefined) {
        throw new Error(
            '`useBroadcastChannelContext` must be used within `BroadcastChannelContextProvider`'
        );
    }

    return context;
};
